import { Checkbox } from '@chakra-ui/react'
import { SystemProps } from '@chakra-ui/styled-system'
import styled from '@emotion/styled'
import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { TActiveColorKey, TInputColorKey } from '~theme/typings'
import { getActiveColorFullPath, getColorValue, getContrastShadeFor } from '~theme/utils'
import { cn } from '~ui/libs/classnames'
import { radiuses } from '~ui/styleGuide/radiuses'
import { shadows } from '~ui/styleGuide/shadows'
import { getSpacingValue, TSingleSpace } from '~ui/styleGuide/spaces'
import { durations } from '~ui/styleGuide/styles'
import { useTheme } from '~ui/theme/ThemeProvider'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'
import { getMarkIcon, TBackgroundIconColor } from '~ui/utils/backgroundIcons'
import { useHoverState } from '~ui/utils/useHoverState'

import { INPUT_GROUP_DIVIDER_CONTRAST } from '../accessories/formAccessories.config'

type TCommonProps = {
  /**
   * where should the checkbox be rendered relative to the label
   */
  checkboxPosition?: 'left' | 'right'
  /**
   * to align the checkbox with the first text line
   */
  checkboxMarginTop?: SystemProps['marginTop']
}

type TInputStyledProps = TCommonProps & {
  /**
   * background color of the unselected radio
   */
  bgColor: string
  /**
   * background color of the selected radio
   */
  selectedBgColor: string
  /**
   * color of the check mark icon
   */
  selectedMarkColor: TBackgroundIconColor
}

type TWrapperStyledProps = {
  /**
   * min-height of the item (input + label)
   */
  minHeight?: SystemProps['minHeight']
  /**
   * padding-bottom of the item (input + label), used to define the spacing between the item content and the divider
   */
  dividerSpacing?: TSingleSpace
  /**
   * color of the divider line between items (bottom border)
   */
  dividerColorValue?: string
}

type TProps = Children &
  TCommonProps &
  Omit<TWrapperStyledProps, 'dividerColorValue'> & {
    /**
     * if true, render bottom border line under each item
     */
    hasDivider?: boolean
    /**
     * value that is passed to the callback when the checkbox is selected
     */
    value: string
    /**
     * whether or not the checkbox is selected
     */
    isChecked?: boolean
    /**
     * for separately controlled checkboxes
     * ! will not work inside a CheckboxGroup
     */
    onChange?: (value: string) => void
    /**
     * makes the checkbox not interactive & replaces cursor
     */
    isDisabled?: boolean
    /**
     * if `true` required attribute will be added
     */
    isRequired?: boolean
    /**
     * background color of the unselected checkbox
     */
    bgColor?: TInputColorKey
    /**
     * background color of the selected checkbox
     * used also to decide the color of the check mark icon
     */
    selectedBgColor?: TActiveColorKey
  }

// get rid of the React error when passing unknown prop to the HTML element
const wrapperPropForwardOptions = {
  shouldForwardProp: (propName: string) =>
    propName !== `minHeight` && propName !== `dividerSpacing` && propName !== `dividerColorValue`,
}

const StyledCheckboxWrapper = styled('div', wrapperPropForwardOptions)<TWrapperStyledProps>`
  /* chakra wrapper around each checkbox */
  min-height: ${({ minHeight }) => minHeight};
  display: grid;

  &.has-divider {
    padding-bottom: ${({ dividerSpacing }) => getSpacingValue(dividerSpacing ?? `none`)};
    border-bottom: 1px solid ${({ dividerColorValue }) => dividerColorValue};
  }

  .chakra-checkbox {
    width: 100%;
    justify-content: space-between;
    cursor: pointer;
  }

  /* chakra wrapper around the checkbox label */
  .chakra-checkbox__label {
    flex-grow: 1;
    margin: 0px;
  }
`

// get rid of the React error when passing unknown prop to the HTML element
const inputPropForwardOptions = {
  shouldForwardProp: (propName: string) =>
    propName !== `selectedBgColor` &&
    propName !== `selectedMarkColor` &&
    propName !== `bgColor` &&
    propName !== `checkboxPosition` &&
    propName !== `checkboxMarginTop`,
}

// this represents just the visual checkbox input (div shaped as a checkbox)
const StyledInputCheckbox = styled(Checkbox, inputPropForwardOptions)<TInputStyledProps>`
  --outline-width: 2px;

  .chakra-checkbox__control {
    --dimensions: 1rem;

    width: var(--dimensions);
    height: var(--dimensions);

    /* align the checkbox with the first text line */
    align-self: flex-start;
    margin-top: ${({ checkboxMarginTop }) => checkboxMarginTop};

    /* move the checkbox on the right side of the label */
    order: ${({ checkboxPosition }) => (checkboxPosition === 'left' ? 0 : 1)};
    border-radius: ${radiuses.small};
    box-shadow: ${shadows.outer.sharpLight};
    background: ${({ bgColor }) => bgColor};

    border: var(--outline-width) solid transparent;
    transition: ${durations.short}ms all;

    > div {
      display: none !important;
    }

    &[data-disabled] {
      cursor: not-allowed;
      border: var(--outline-width) solid transparent;
    }

    &[data-focus] {
      border: var(--outline-width) solid ${({ theme }) => theme.activeColors.info.normal};
    }

    &[data-checked] {
      background: ${({ selectedBgColor }) => selectedBgColor};

      // https://stackoverflow.com/a/29276505
      &::after {
        content: '';
        width: 100%;
        height: 100%;

        ${({ selectedMarkColor }) =>
          `background-image: url('data:image/svg+xml; utf8, ${getMarkIcon(selectedMarkColor)}');`};
      }
    }
  }
`

export const InputCheckbox: React.FC<TProps> = ({
  value,
  children,
  onChange,
  minHeight,
  isChecked,
  isDisabled,
  hasDivider,
  dividerSpacing,
  bgColor = `inputOnPrimary`,
  isRequired = false,
  checkboxPosition = `right`,
  checkboxMarginTop = `0.25rem`,
  selectedBgColor = `success`,
}) => {
  const theme = useTheme()
  const { dividerColors } = useBgColorCtx()
  const { isHovered, setHoverStart, setHoverEnd } = useHoverState()

  // color of the unchecked checkbox
  const bgColorValue = React.useMemo(() => {
    const fullKey = getActiveColorFullPath({ bgColor, isDisabled, isHovered })
    return getColorValue(theme)(fullKey)
  }, [bgColor, isDisabled, isHovered, theme])

  const selectedBgColorValue = React.useMemo(() => {
    const fullKey = getActiveColorFullPath({ bgColor: selectedBgColor, isDisabled, isHovered })
    return getColorValue(theme)(fullKey)
  }, [isDisabled, isHovered, selectedBgColor, theme])

  const bgColorKey = isChecked ? selectedBgColor : bgColor
  const themeDesignProps = {
    [THEME_KEY_ATTRIBUTE]: getActiveColorFullPath({ bgColor: bgColorKey }),
  }

  const selectedMarkColor: TBackgroundIconColor =
    getContrastShadeFor(selectedBgColorValue) === `dark` ? `black` : `white`

  return (
    <StyledCheckboxWrapper
      className={cn({ 'has-divider': hasDivider })}
      minHeight={minHeight}
      onPointerLeave={setHoverEnd}
      onPointerEnter={setHoverStart}
      dividerSpacing={dividerSpacing}
      dividerColorValue={dividerColors[INPUT_GROUP_DIVIDER_CONTRAST]}
    >
      <StyledInputCheckbox
        {...themeDesignProps}
        value={value}
        checked={isChecked}
        isChecked={isChecked}
        disabled={isDisabled} // style
        isDisabled={isDisabled} // event handlers
        bgColor={bgColorValue}
        onChange={() => onChange?.(value)}
        checkboxPosition={checkboxPosition}
        checkboxMarginTop={checkboxMarginTop}
        selectedMarkColor={selectedMarkColor}
        selectedBgColor={selectedBgColorValue}
        isRequired={isRequired}
      >
        {children}
      </StyledInputCheckbox>
    </StyledCheckboxWrapper>
  )
}
